import React, { useState, useEffect } from 'react';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';
import styled from 'styled-components';
import { FaCopy, FaTimes, FaUpload, FaTrash, FaEdit, FaCheck, FaTimes as FaCancel, FaSync } from 'react-icons/fa';

const SettingsContainer = styled.div`
  padding: 0 5% 50px 5%;
  min-height: 100vh;
  background-color: #0b0614;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  background: linear-gradient(90deg, var(--gradient-cyan), var(--gradient-pink));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const MediaGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 15px;
    margin-top: 20px;
    max-height: 600px;
    overflow-y: auto;
    padding: 10px;
`;

const MediaItem = styled.div`
    position: relative;
    aspect-ratio: 1;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    border: 2px solid ${props => props.$selected ? 'var(--gradient-pink)' : 'transparent'};
    transition: all 0.3s ease;
    
    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 5px 20px rgba(255, 0, 127, 0.3);
        border-color: var(--gradient-pink);
    }
    
    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const MediaInfo = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.9), transparent);
    padding: 8px;
    color: white;
    font-size: 0.75rem;
    opacity: 0;
    transition: opacity 0.3s ease;
    
    ${MediaItem}:hover & {
        opacity: 1;
    }
`;

const MediaModal = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.9);
    display: ${props => props.$show ? 'flex' : 'none'};
    align-items: center;
    justify-content: center;
    z-index: 10000;
    padding: 20px;
`;

const ModalContent = styled.div`
    background: var(--glass-bg);
    border: 1px solid var(--border-color);
    border-radius: 12px;
    padding: 25px;
    max-width: 600px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    
    h3 {
        margin: 0;
        color: var(--primary-color);
    }
`;

const CloseButton = styled.button`
    background: transparent;
    border: none;
    color: var(--primary-color);
    font-size: 1.5rem;
    cursor: pointer;
    padding: 5px 10px;
    
    &:hover {
        color: var(--gradient-pink);
    }
`;

const PathInput = styled.input`
    width: 100%;
    padding: 10px;
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    color: var(--primary-color);
    font-size: 0.9rem;
    font-family: monospace;
    margin-bottom: 10px;
    
    &:focus {
        outline: none;
        border-color: var(--gradient-pink);
    }
`;

const CopyButton = styled.button`
    padding: 8px 15px;
    background: var(--gradient-pink);
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 10px;
    
    &:hover {
        opacity: 0.9;
    }
`;

const MediaSearch = styled.input`
    width: 100%;
    padding: 10px;
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    color: var(--primary-color);
    font-size: 1rem;
    margin-bottom: 15px;
    
    &:focus {
        outline: none;
        border-color: var(--gradient-pink);
    }
`;

const ActionBar = styled.div`
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
    flex-wrap: wrap;
`;

const ActionButton = styled.button`
    padding: 10px 20px;
    background: ${props => props.$variant === 'danger' ? '#ff4757' : 'var(--gradient-pink)'};
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s ease;
    
    &:hover {
        opacity: 0.9;
        transform: translateY(-2px);
    }
    
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none;
    }
`;

const UploadInput = styled.input`
    display: none;
`;

const EditInput = styled.input`
    width: 100%;
    padding: 8px;
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid var(--border-color);
    border-radius: 6px;
    color: var(--primary-color);
    font-size: 0.9rem;
    margin-top: 5px;
    
    &:focus {
        outline: none;
        border-color: var(--gradient-pink);
    }
`;

const AdminMediaLibraryPage = () => {
    const [media, setMedia] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingName, setEditingName] = useState(null);
    const [newName, setNewName] = useState('');

    useEffect(() => {
        loadMedia();
    }, []);

    const loadMedia = async () => {
        setLoading(true);
        try {
            const response = await fetch(getApiUrl(API_ENDPOINTS.GET_MEDIA_LIBRARY), {
                credentials: 'include'
            });
            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setMedia(data.media || []);
                }
            }
        } catch (error) {
            console.error('Medya yüklenirken hata:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredMedia = media.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.path.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            alert('Kopyalandı!');
        });
    };

    const getProxyPath = (mediaItem) => {
        // Eğer original_path varsa onu kullan, yoksa name'i kullan
        const path = mediaItem.original_path || mediaItem.path || '/' + mediaItem.name;
        const cleanPath = path.startsWith('/') ? path : '/' + path;
        return `${getApiUrl(API_ENDPOINTS.PROXY_IMAGE)}?path=${encodeURIComponent(cleanPath)}&size=w500`;
    };

    const getImageUrl = (mediaItem) => {
        // Cache'de varsa local URL
        if (mediaItem.cached) {
            return mediaItem.url;
        } else {
            // Cache'de yoksa proxy kullan (proxy otomatik cache alır)
            return getProxyPath(mediaItem);
        }
    };

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            alert('Sadece resim dosyaları yüklenebilir!');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await fetch(getApiUrl(API_ENDPOINTS.UPLOAD_MEDIA), {
                method: 'POST',
                credentials: 'include',
                body: formData
            });

            const data = await response.json();
            if (data.success) {
                alert('Dosya başarıyla yüklendi!');
                loadMedia();
            } else {
                alert('Yükleme hatası: ' + (data.message || 'Bilinmeyen hata'));
            }
        } catch (error) {
            console.error('Yükleme hatası:', error);
            alert('Yükleme sırasında hata oluştu!');
        }
    };

    const handleDelete = async (mediaItem) => {
        if (!window.confirm(`"${mediaItem.name}" dosyasını silmek istediğinize emin misiniz?`)) {
            return;
        }

        try {
            const response = await fetch(getApiUrl(API_ENDPOINTS.DELETE_MEDIA), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ path: mediaItem.path })
            });

            const data = await response.json();
            if (data.success) {
                alert('Dosya başarıyla silindi!');
                loadMedia();
                if (selectedMedia && selectedMedia.path === mediaItem.path) {
                    setSelectedMedia(null);
                }
            } else {
                alert('Silme hatası: ' + (data.message || 'Bilinmeyen hata'));
            }
        } catch (error) {
            console.error('Silme hatası:', error);
            alert('Silme sırasında hata oluştu!');
        }
    };

    const handleRename = async (mediaItem, newFileName) => {
        if (!newFileName || newFileName.trim() === '') {
            alert('Dosya adı boş olamaz!');
            return;
        }

        // Uzantıyı koru
        const extension = mediaItem.name.split('.').pop();
        const finalName = newFileName.endsWith('.' + extension) ? newFileName : newFileName + '.' + extension;

        try {
            const response = await fetch(getApiUrl(API_ENDPOINTS.RENAME_MEDIA), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ 
                    oldPath: mediaItem.path,
                    newName: finalName
                })
            });

            const data = await response.json();
            if (data.success) {
                alert('Dosya adı başarıyla değiştirildi!');
                setEditingName(null);
                setNewName('');
                loadMedia();
                if (selectedMedia && selectedMedia.path === mediaItem.path) {
                    setSelectedMedia(null);
                }
            } else {
                alert('Yeniden adlandırma hatası: ' + (data.message || 'Bilinmeyen hata'));
            }
        } catch (error) {
            console.error('Yeniden adlandırma hatası:', error);
            alert('Yeniden adlandırma sırasında hata oluştu!');
        }
    };

    const startEditing = (mediaItem) => {
        const nameWithoutExt = mediaItem.name.replace(/\.[^/.]+$/, '');
        setEditingName(mediaItem.path);
        setNewName(nameWithoutExt);
    };

    if (loading) {
        return (
            <SettingsContainer>
                <Header>
                    <Title>Medya Kütüphanesi</Title>
                </Header>
                <div style={{ color: 'var(--secondary-color)', textAlign: 'center', padding: '20px' }}>
                    Medya yükleniyor...
                </div>
            </SettingsContainer>
        );
    }

    return (
        <SettingsContainer>
            <Header>
                <Title>Medya Kütüphanesi</Title>
            </Header>

            <ActionBar>
                <label>
                    <UploadInput
                        type="file"
                        accept="image/*"
                        onChange={handleUpload}
                    />
                    <ActionButton as="div" style={{ cursor: 'pointer' }}>
                        <FaUpload /> Dosya Yükle
                    </ActionButton>
                </label>
                <ActionButton onClick={loadMedia}>
                    <FaSync /> Yenile
                </ActionButton>
            </ActionBar>

            <MediaSearch
                type="text"
                placeholder="Medya ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div style={{ marginBottom: '10px', color: 'var(--secondary-color)', fontSize: '0.9rem' }}>
                Toplam {filteredMedia.length} dosya
            </div>

            {filteredMedia.length === 0 ? (
                <div style={{ color: 'var(--secondary-color)', textAlign: 'center', padding: '40px' }}>
                    {searchTerm ? 'Arama sonucu bulunamadı' : 'Henüz medya dosyası yok'}
                </div>
            ) : (
                <MediaGrid>
                    {filteredMedia.map((item, index) => (
                        <MediaItem
                            key={index}
                            onClick={() => setSelectedMedia(item)}
                            style={{
                                borderColor: item.cached ? (item.$selected ? 'var(--gradient-pink)' : 'transparent') : '#ffa500',
                                borderWidth: item.cached ? '2px' : '3px',
                                borderStyle: item.cached ? 'solid' : 'dashed'
                            }}
                        >
                            <img 
                                src={getImageUrl(item)} 
                                alt={item.name}
                                onError={(e) => {
                                    // Önce proxy'yi dene
                                    e.target.src = getProxyPath(item);
                                    e.target.onerror = () => {
                                        // Proxy de çalışmazsa placeholder göster
                                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iIzExMSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM2NjYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5SZXNpbSBZb2s8L3RleHQ+PC9zdmc+';
                                    };
                                }}
                            />
                            <MediaInfo>
                                <div>{item.name}</div>
                                <div style={{ fontSize: '0.7rem', opacity: 0.8 }}>
                                    {item.cached ? `${(item.size / 1024).toFixed(1)} KB` : 'Cache yok'}
                                </div>
                                {item.type && (
                                    <div style={{ fontSize: '0.65rem', opacity: 0.7, marginTop: '2px' }}>
                                        {item.type === 'poster' ? 'Poster' : item.type === 'backdrop' ? 'Backdrop' : 'Thumbnail'}
                                    </div>
                                )}
                            </MediaInfo>
                        </MediaItem>
                    ))}
                </MediaGrid>
            )}

            <MediaModal $show={selectedMedia !== null}>
                <ModalContent>
                    <ModalHeader>
                        <h3>Medya Detayları</h3>
                        <CloseButton onClick={() => {
                            setSelectedMedia(null);
                            setEditingName(null);
                            setNewName('');
                        }}>
                            <FaTimes />
                        </CloseButton>
                    </ModalHeader>

                    {selectedMedia && (
                        <>
                            <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                                <img 
                                    src={getImageUrl(selectedMedia)} 
                                    alt={selectedMedia.name}
                                    style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px' }}
                                    onError={(e) => {
                                        e.target.src = getProxyPath(selectedMedia);
                                        e.target.onerror = () => {
                                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iIzExMSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM2NjYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5SZXNpbSBZb2s8L3RleHQ+PC9zdmc+';
                                        };
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, color: 'var(--primary-color)' }}>
                                    Dosya Adı
                                </label>
                                {editingName === selectedMedia.path ? (
                                    <div>
                                        <EditInput
                                            type="text"
                                            value={newName}
                                            onChange={(e) => setNewName(e.target.value)}
                                            autoFocus
                                        />
                                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                            <ActionButton 
                                                onClick={() => handleRename(selectedMedia, newName)}
                                                style={{ padding: '6px 12px', fontSize: '0.85rem' }}
                                            >
                                                <FaCheck /> Kaydet
                                            </ActionButton>
                                            <ActionButton 
                                                onClick={() => {
                                                    setEditingName(null);
                                                    setNewName('');
                                                }}
                                                style={{ padding: '6px 12px', fontSize: '0.85rem', background: '#6c757d' }}
                                            >
                                                <FaCancel /> İptal
                                            </ActionButton>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                        <PathInput 
                                            type="text" 
                                            value={selectedMedia.name} 
                                            readOnly 
                                            style={{ flex: 1 }}
                                        />
                                        <ActionButton 
                                            onClick={() => startEditing(selectedMedia)}
                                            style={{ padding: '8px 15px', fontSize: '0.85rem' }}
                                        >
                                            <FaEdit /> Düzenle
                                        </ActionButton>
                                    </div>
                                )}
                            </div>

                            <div style={{ marginBottom: '10px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, color: 'var(--primary-color)' }}>
                                    Dosya Yolu
                                </label>
                                <PathInput 
                                    type="text" 
                                    value={selectedMedia.path} 
                                    readOnly 
                                />
                            </div>

                            {selectedMedia.original_path && selectedMedia.original_path !== selectedMedia.path && (
                                <div style={{ marginBottom: '10px' }}>
                                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, color: 'var(--primary-color)' }}>
                                        Orijinal Path (Veritabanı)
                                    </label>
                                    <PathInput 
                                        type="text" 
                                        value={selectedMedia.original_path} 
                                        readOnly 
                                    />
                                </div>
                            )}

                            <div style={{ marginBottom: '10px', padding: '10px', background: selectedMedia.cached ? 'rgba(46, 213, 115, 0.1)' : 'rgba(255, 165, 0, 0.1)', border: `1px solid ${selectedMedia.cached ? '#2ed573' : '#ffa500'}`, borderRadius: '6px' }}>
                                <div style={{ fontSize: '0.9rem', color: selectedMedia.cached ? '#2ed573' : '#ffa500', fontWeight: 600 }}>
                                    {selectedMedia.cached ? 'Cache\'de Mevcut' : 'Cache\'de Yok - Sadece Veritabanında'}
                                </div>
                                {selectedMedia.type && (
                                    <div style={{ fontSize: '0.8rem', color: 'var(--secondary-color)', marginTop: '5px' }}>
                                        Tip: {selectedMedia.type === 'poster' ? 'Poster' : selectedMedia.type === 'backdrop' ? 'Backdrop' : 'Thumbnail'}
                                    </div>
                                )}
                            </div>

                            <div style={{ marginBottom: '10px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, color: 'var(--primary-color)' }}>
                                    Tam URL
                                </label>
                                <PathInput 
                                    type="text" 
                                    value={selectedMedia.url} 
                                    readOnly 
                                />
                            </div>

                            <div style={{ marginBottom: '10px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, color: 'var(--primary-color)' }}>
                                    Proxy Path (Kullanım için)
                                </label>
                                <PathInput 
                                    type="text" 
                                    value={getProxyPath(selectedMedia)} 
                                    readOnly 
                                />
                            </div>

                            <CopyButton onClick={() => copyToClipboard(selectedMedia.url)}>
                                <FaCopy /> URL'yi Kopyala
                            </CopyButton>

                            <CopyButton onClick={() => copyToClipboard(getProxyPath(selectedMedia))} style={{ marginTop: '10px' }}>
                                <FaCopy /> Proxy Path'i Kopyala
                            </CopyButton>

                            <ActionButton 
                                $variant="danger"
                                onClick={() => handleDelete(selectedMedia)}
                                style={{ marginTop: '15px', width: '100%' }}
                            >
                                <FaTrash /> Dosyayı Sil
                            </ActionButton>

                            <div style={{ marginTop: '15px', fontSize: '0.85rem', color: 'var(--secondary-color)' }}>
                                <div>Boyut: {(selectedMedia.size / 1024).toFixed(2)} KB</div>
                                <div>Değiştirilme: {selectedMedia.modified}</div>
                            </div>
                        </>
                    )}
                </ModalContent>
            </MediaModal>
        </SettingsContainer>
    );
};

export default AdminMediaLibraryPage;

